/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, DoCheck, Input, OnChanges} from '@angular/core';
import template from './line-item-creative-list.component.html';

import {AdnResourceListService} from '../common/controller/adn-resource-list.service';
import {asUrlGroupList, asUrlList} from '../creative/creative-url-utils';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';

/** Embedded creative table on line item / creative set edit — not a standalone list page. */
@Component({
  standalone: false,
  selector: 'adn-line-item-creative-list',
  template
})
export class LineItemCreativeListComponent implements OnChanges, DoCheck {
  @Input() resource: any;
  @Input() readOnly: boolean;

  showCreativeSizes = false;

  /**
   * Buffered for template — list-helper / dim-check fill can land during parent
   * checkNoChanges (NG0100 on items.length, availableFixed, unmatchedCreatives).
   */
  listQuery: any;
  listItems: any[] = [];
  listTotalCount = 0;
  listPageSize = 25;
  isNewResource = true;
  hasItems = false;
  showPagination = false;
  hasDeliverableSizes = false;
  availableFixed: any[] = [];
  availableRanges: any[] = [];
  /** Keyed by creative id — buffered so Undeliverable *ngIf cannot flip mid-CD. */
  unmatchedCreatives: Record<string, any> = {};

  readonly sortHeaderParams = [
    {param: 'name', label: 'creative'},
    {label: 'info', param: '', sortable: false},
    {param: '', label: 'targeting', sortable: false}
  ];

  readonly itemStateName = 'app.adv.creatives.creative';
  readonly newItemStateName = 'app.adv.creatives.new';
  readonly layoutItemStateName = 'app.adm.layouts.layout';
  readonly externalDemandSourceItemStateName = 'app.adm.external-demand-sources.external-demand-source';

  private lastQuery: any;
  private lastItems: any;
  private lastTotalCount: any;
  private lastIsNew: any;
  private lastPageSize: any;
  private lastChecksDone: any;
  private lastAvailableFixed: any;
  private lastAvailableRanges: any;
  private lastUnmatchedCreatives: any;
  private syncScheduled = false;
  private initialized = false;

  constructor(
    private readonly listService: AdnResourceListService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnChanges(): void {
    this.wireResourceList();
    this.scheduleListSync();
  }

  ngDoCheck(): void {
    this.wireResourceList();
    this.scheduleListSync();
  }

  private wireResourceList(): void {
    if (this.resource?.makeQuery) {
      this.listService.ensureAngularList(this.resource);
    }
  }

  private scheduleListSync(): void {
    const resource = this.resource;
    const query = resource?.query;
    const items = resource?.items;
    const totalCount = resource?.totalCount;
    const isNew = !!resource?.isNew;
    const pageSize = resource?.pageSize;
    const checksDone = !!resource?.checksDone;
    const availableFixed = resource?.availableFixed;
    const availableRanges = resource?.availableRanges;
    const unmatchedCreatives = resource?.unmatchedCreatives;

    if (
      query === this.lastQuery &&
      items === this.lastItems &&
      totalCount === this.lastTotalCount &&
      isNew === this.lastIsNew &&
      pageSize === this.lastPageSize &&
      checksDone === this.lastChecksDone &&
      availableFixed === this.lastAvailableFixed &&
      availableRanges === this.lastAvailableRanges &&
      unmatchedCreatives === this.lastUnmatchedCreatives
    ) {
      return;
    }

    // First paint: apply sync so isNew / empty state is correct immediately.
    if (!this.initialized) {
      this.initialized = true;
      this.applyListSnapshot();
      return;
    }

    if (this.syncScheduled) {
      return;
    }
    this.syncScheduled = true;
    // Defer past checkNoChanges — parent CD often overlaps list / dim-check settle.
    deferMarkForCheck(this.cdr, () => {
      this.syncScheduled = false;
      this.applyListSnapshot();
    });
  }

  private applyListSnapshot(): void {
    const resource = this.resource;
    this.lastQuery = resource?.query;
    this.lastItems = resource?.items;
    this.lastTotalCount = resource?.totalCount;
    this.lastIsNew = !!resource?.isNew;
    this.lastPageSize = resource?.pageSize;
    this.lastChecksDone = !!resource?.checksDone;
    this.lastAvailableFixed = resource?.availableFixed;
    this.lastAvailableRanges = resource?.availableRanges;
    this.lastUnmatchedCreatives = resource?.unmatchedCreatives;

    this.listQuery = this.lastQuery;
    this.listItems = this.lastItems || [];
    this.listTotalCount = this.lastTotalCount || 0;
    this.listPageSize = this.lastPageSize || 25;
    this.isNewResource = this.lastIsNew;
    this.hasItems = this.listItems.length > 0;
    this.showPagination = this.listTotalCount > this.listPageSize || this.listTotalCount > 5;
    this.availableFixed = this.lastAvailableFixed || [];
    this.availableRanges = this.lastAvailableRanges || [];
    this.hasDeliverableSizes = this.lastChecksDone &&
      (!!this.availableFixed.length || !!this.availableRanges.length);
    // dimCheck starts as [] then becomes a keyed object — only the object is usable.
    this.unmatchedCreatives = this.lastUnmatchedCreatives && !Array.isArray(this.lastUnmatchedCreatives)
      ? this.lastUnmatchedCreatives
      : {};
  }

  hideBulkSelect(): boolean {
    return !!this.resource?.readOnly || !!this.resource?.isAxPublisher;
  }

  creativeLinkParams(creative: any): any {
    return {
      fromLineItem: !this.resource?.isCreativeSet,
      fromCreativeSet: !!this.resource?.isCreativeSet,
      id: creative.id
    };
  }

  newCreativeParams(): any {
    const model = this.resource?.model || {};
    return {
      fromCreativeSet: !!this.resource?.isCreativeSet,
      fromLineItem: !this.resource?.isCreativeSet,
      modelName: model.name,
      modelId: model.id,
      advertiserName: model.advertiser?.name,
      advertiserId: model.advertiser?.id
    };
  }

  isUnmatchedCreative(creative: any): boolean {
    return !!this.unmatchedCreatives?.[creative.id];
  }

  undeliverableMessage(creative: any): string {
    return `The targeted inventory does not support the delivery of ${creative.width}×${creative.height} creatives`;
  }

  layoutLinkDisabled(): boolean {
    return this.resource?.perms?.MANAGE_LAYOUTS !== true;
  }

  externalDemandLinkDisabled(): boolean {
    return this.resource?.perms?.MANAGE_SYSTEM !== true;
  }

  constraintUrls(creative: any): string[] {
    return asUrlList(creative?.constraintsToUrls);
  }

  assetHtmlUrlGroups(creative: any): string[][] {
    return asUrlGroupList(creative?.assetHtmlUrls);
  }
}
